import React, { useState, useMemo, useEffect } from 'react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { getTicketMetadata, type TicketMetadata } from '../services/scansApiService';
import { Download, Printer } from 'lucide-react';

// --- Utility for Tailwind classes ---
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// --- Types & Configuration ---
type ColumnType = 'text' | 'number' | 'select';

interface SpreadsheetProps {
  storageKey?: string;
  onLoadsSumChange?: (sum: number) => void;
  onDataChange?: (data: Record<string, any>[]) => void;
  selectedDate?: string;
}

export interface Option {
  label: string;
  value: string;
  uom?: string;
  inventoryId?: string;
}

interface ColumnDef {
  id: string;
  field: string;
  type: ColumnType;
  options?: Option[];
  width?: string;
}

// Initial columns defined without options (they will load from API)
const INITIAL_COLUMNS: ColumnDef[] = [
  { id: 'Material Type', field: 'material', type: 'select', options: [], width: 'w-48' },
  { id: 'Supplier', field: 'supplier', type: 'select', options: [], width: 'w-48' },
  { id: 'Hauler', field: 'hauler', type: 'select', options: [], width: 'w-48' },
  { id: 'Loads', field: 'loads', type: 'number', width: 'w-32' },
  { id: 'Qty', field: 'qty', type: 'number', width: 'w-32' },
];

const INITIAL_ROWS = 40;

// --- Main Component ---
export default function Spreadsheet({ storageKey = 'default_spreadsheet', onLoadsSumChange, onDataChange, selectedDate }: SpreadsheetProps) {
  const [columns, setColumns] = useState<ColumnDef[]>(INITIAL_COLUMNS);
  
const [data, setData] = useState<Record<string, any>[]>(() => {
    const savedData = localStorage.getItem(storageKey);
    if (savedData) {
      try {
        return JSON.parse(savedData);
      } catch (error) {
        console.error("Failed to parse local storage data:", error);
      }
    }
    
    return Array.from({ length: INITIAL_ROWS }).map((_, index) => {
      const row: Record<string, any> = { id: index };
      INITIAL_COLUMNS.forEach((col) => {
        row[col.field] = '';
      });
      return row;
    });
  });

  const [selectedCell, setSelectedCell] = useState<{ r: number; c: string } | null>(null);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(data));

    if (onDataChange) {
      onDataChange(data);
    }
  }, [data, storageKey]);

  // Fetch Dropdown Metadata
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const metadata = await getTicketMetadata();
        if (!metadata) return;

        setColumns(prevColumns => prevColumns.map(col => {
          if (col.field === 'material') {
            return {
              ...col,
              options: metadata.coverSheetInventoryItems.map(i => ({
                label: i.inventoryDesc,
                value: i.inventoryDesc,
                uom: i.uom,
                inventoryId: i.inventoryId
              }))
            };
          }
          if (col.field === 'supplier') {
            return {
              ...col,
              options: metadata.vendors.map(v => ({
                label: v.vendorIddesc, 
                value: v.vendorId
              }))
            };
          }
          if (col.field === 'hauler') {
            return {
              ...col,
              options: metadata.freightVendors.map(f => ({
                label: f.freightVendorIddesc, 
                value: f.freightVendorId
              }))
            };
          }
          return col;
        }));
      } catch (error) {
        console.error("Failed to load spreadsheet options", error);
      }
    };

    fetchMetadata();
  }, []);

  const handleChange = (rowIndex: number, field: string, value: string) => {
    setData((prev) => {
      const newData = [...prev];
      const updatedRow = { ...newData[rowIndex], [field]: value };

      if (field === 'material') {
        const materialCol = columns.find(c => c.field === 'material');
        const selectedOpt = materialCol?.options?.find(o => o.value === value);
        updatedRow.uom = selectedOpt?.uom || '';
        updatedRow.inventoryId = selectedOpt?.inventoryId || '';
      }

      newData[rowIndex] = updatedRow;
      return newData;
    });
  };

  const aggregates = useMemo(() => {
    const sums: Record<string, number> = {};
    columns.forEach((col) => {
      if (col.type === 'number') {
        const total = data.reduce((acc, row) => {
          const val = parseFloat(row[col.field]);
          return acc + (isNaN(val) ? 0 : val);
        }, 0);
        sums[col.field] = total;
      }
    });
    return sums;
  }, [data, columns]);

  useEffect(() => {
    if (onLoadsSumChange) {
      onLoadsSumChange(aggregates['loads'] || 0);
    }
  }, [aggregates, onLoadsSumChange]);

  const downloadCSV = () => {
    const cleanData = data.filter((row) => 
       columns.some(col => row[col.field] !== '')
    );

    if (cleanData.length === 0) {
      alert("No data to export!");
      return;
    }

    const headers = columns.map((col) => col.field).join(',');

    const csvRows = cleanData.map((row) => {
      return columns.map((col) => {
        let cellValue = String(row[col.field] || '');
        if (col.type === 'select' && col.options) {
          const match = col.options.find(o => String(o.value) === cellValue);
          if (match) cellValue = match.label;
        }
        cellValue = cellValue.replace(/"/g, '""');
        if (cellValue.search(/("|,|\n)/g) >= 0) {
          cellValue = `"${cellValue}"`;
        }
        return cellValue;
      }).join(',');
    });

    const dateLine = selectedDate ? `Date:,${selectedDate}\n` : '';
    const csvString = dateLine + [headers, ...csvRows].join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `spreadsheet_${selectedDate || 'data'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePrint = () => {
    const cleanData = data.filter((row) =>
      columns.some(col => row[col.field] !== '')
    );

    if (cleanData.length === 0) {
      alert("No data to print!");
      return;
    }

    const headerCells = columns.map(col => `<th style="border:1px solid #ccc;padding:6px 12px;text-align:left;background:#f0f0f0;white-space:nowrap;">${col.id}</th>`).join('');

    const bodyRows = cleanData.map((row, i) => {
      const bg = i % 2 === 1 ? '#f7f7f7' : '#fff';
      const cells = columns.map(col => {
        let val = String(row[col.field] || '');
        if (col.type === 'select' && col.options) {
          const match = col.options.find(o => o.value === val);
          if (match) val = match.label;
        }
        return `<td style="border:1px solid #ddd;padding:6px 12px;white-space:nowrap;">${val}</td>`;
      }).join('');
      return `<tr style="background:${bg}">${cells}</tr>`;
    }).join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
      <head>
        <title>Spreadsheet</title>
        <style>
          @page { size: landscape; margin: 0.5in; }
          body { font-family: sans-serif; font-size: 12px; margin: 0; padding: 16px; }
          table { border-collapse: collapse; width: 100%; }
        </style>
      </head>
      <body>
        ${selectedDate ? `<p style="font-size:14px;font-weight:bold;margin-bottom:8px;">Date: ${selectedDate}</p>` : ''}
        <table>
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
        <script>window.onload = function() { window.print(); }<\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="flex flex-col h-full bg-white text-xs font-sans">
      {/* Toolbar */}
      <div className="bg-[#f8f9fa] border-b border-[#e1e1e1] p-2 flex items-center justify-end shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={downloadCSV}
            className="text-[#107c41] hover:bg-[#f3f2f1] px-4 py-1.5 rounded transition-colors font-semibold"
            title="Download CSV"
          >
            <Download size={16} />
          </button>
          <button
            onClick={handlePrint}
            className="text-[#107c41] hover:bg-[#f3f2f1] px-4 py-1.5 rounded transition-colors font-semibold"
            title="Print"
          >
            <Printer size={16} />
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto relative">
        <table className="border-collapse w-full table-fixed">
          <thead className="sticky top-0 z-20 bg-[#f8f9fa]">
            <tr>
              <th className="w-10 h-6 border-b border-r border-[#c7c7c7] bg-[#f8f9fa] sticky left-0 z-30">
                 <div className="w-0 h-0 border-l-[10px] border-t-[10px] border-l-transparent border-t-gray-400 absolute bottom-1 right-1"></div>
              </th>
              {columns.map((col) => (
                <th key={col.id} className={cn("h-6 font-normal text-gray-700 border-r border-b border-[#c7c7c7] select-none text-center relative hover:bg-[#e1e1e1] transition-colors", col.width)}>
                  {col.id}
                  <div className="absolute right-0 top-0 bottom-0 w-[2px] cursor-col-resize hover:bg-[#107c41]"></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rIndex) => {
              const rowBg = rIndex % 2 === 1 ? "bg-[#f0f6fc]" : "bg-white";
              return (
              <tr key={row.id} className="h-6">
                <td className={cn("sticky left-0 z-10 text-center border-r border-b border-[#c7c7c7] text-gray-600 select-none", rowBg)}>
                  {rIndex + 1}
                </td>
                {columns.map((col) => (
                  <td
                    key={`${row.id}-${col.id}`}
                    className={cn(
                      "border-r border-b border-[#e1e1e1] p-0 relative focus-within:ring-2 focus-within:ring-[#107c41] focus-within:z-10",
                      rowBg,
                      selectedCell?.r === rIndex && selectedCell?.c === col.id ? "ring-2 ring-[#107c41] z-10" : ""
                    )}
                    onClick={() => setSelectedCell({ r: rIndex, c: col.id })}
                  >
                    <CellInput
                      type={col.type}
                      value={row[col.field]}
                      options={col.options}
                      onChange={(val) => handleChange(rIndex, col.field, val)}
                    />
                  </td>
                ))}
              </tr>
              );
            })}
          </tbody>
          <tfoot className="sticky bottom-0 z-20 bg-[#f8f9fa] font-bold shadow-[0_-1px_3px_rgba(0,0,0,0.1)]">
             <tr className="h-8">
                <td className="sticky left-0 z-30 bg-[#f8f9fa] border-r border-t border-[#c7c7c7] text-center text-[#107c41]">∑</td>
                {columns.map((col) => (
                  <td key={`sum-${col.id}`} className="border-r border-t border-[#c7c7c7] px-2 text-right text-gray-800 bg-[#f8f9fa]">
                    {col.type === 'number' && <span>{parseFloat(aggregates[col.field].toFixed(4))}</span>}
                  </td>
                ))}
             </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// --- Sub-components: Cell Input Logic ---

interface CellProps {
  type: ColumnType;
  value: string;
  options?: Option[];
  onChange: (val: string) => void;
}

const CellInput = ({ type, value, options, onChange }: CellProps) => {
  if (type === 'select' && options) {
    return <SearchableSelect value={value} options={[...options].sort((a,b) => a.label.localeCompare(b.label))} onChange={onChange} />;
  }

  return (
    <input
      type={type === 'number' ? 'number' : 'text'}
      className={cn(
        "w-full h-full border-none outline-none px-2 text-gray-800 font-sans text-xs bg-transparent",
        type === 'number' ? "text-right" : "text-left"
      )}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

// --- Custom Searchable Select Component ---

interface SearchableSelectProps {
  value: string; // This is the ID
  options: Option[];
  onChange: (val: string) => void;
}

const SearchableSelect = ({ value, options, onChange }: SearchableSelectProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Sync the display label when the underlying ID (value) changes or options load
  useEffect(() => {
    const selectedOpt = options.find((o) => o.value === value);
    setInputValue(selectedOpt ? selectedOpt.label : '');
  }, [value, options]);

  // Filter based on the label (Description)
  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Tab' || e.key === 'Enter') {
      if (filteredOptions.length > 0 && inputValue !== '') {
        // Find exact match first, otherwise grab the closest top option
        const exactMatch = filteredOptions.find(o => o.label.toLowerCase() === inputValue.toLowerCase());
        const targetOpt = exactMatch || filteredOptions[0];
        
        onChange(targetOpt.value); // Save ID
        setInputValue(targetOpt.label); // Show Description
      } else {
        onChange('');
        setInputValue('');
      }
      setIsOpen(false);
    }
  };

  const handleBlur = () => {
    setTimeout(() => setIsOpen(false), 150);
    
    const exactMatch = options.find((o) => o.label.toLowerCase() === inputValue.toLowerCase());

    if (exactMatch) {
      onChange(exactMatch.value);
      setInputValue(exactMatch.label);
    } else {
      onChange('');
      setInputValue('');
    }
  };

  return (
    <div className="relative w-full h-full">
      <input
        type="text"
        className="w-full h-full border-none outline-none px-2 text-gray-800 font-sans text-xs bg-transparent"
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          setIsOpen(true);
        }}
        onFocus={(e) => {
          setIsOpen(true);
          e.target.select(); // Highlight text for quick overwriting
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      />
      {isOpen && (
        <ul className="absolute z-50 w-full bg-white border border-gray-300 shadow-md max-h-40 overflow-auto top-full left-0">
          {filteredOptions.map((opt) => (
            <li
              key={`${opt.value}-${opt.label}`}
              className="px-2 py-1.5 hover:bg-[#107c41] hover:text-white cursor-default text-xs"
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(opt.value);
                setInputValue(opt.label);
                setIsOpen(false);
              }}
            >
              {opt.label}
            </li>
          ))}
          {filteredOptions.length === 0 && (
            <li className="px-2 py-1.5 text-gray-400 text-xs italic">No match</li>
          )}
        </ul>
      )}
    </div>
  );
};